import React, { useState, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import EKYCCardsUnitsMandal from "./EKYCCardsUnitsMandal";

const EKYCCardsUnitsDistricts = ({ distMonth, distYear, distMonthName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [schemeData, setSchemeData] = useState([]);
  const [districtData, setDistrictData] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  const getSchemeTotals = (schemeId, fieldPrefix) => {
    return districtData.reduce((sum, item) => {
      return sum + Number(item[`${fieldPrefix}_${schemeId}`] || 0);
    }, 0);
  };

  const handleDistClick = (dist_code, dist_name_en) => {
    setSelectedDistrict({
      distCode: dist_code,
      distName: dist_name_en,
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [schemeRes, districtRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_URL}/api/ekyc-info/ekyc-card-units-scheme?distMonth=${distMonth}&distYear=${distYear}`
        ),
        fetch(
          `${process.env.REACT_APP_URL}/api/ekyc-info/ekyc-district-card-units?distMonth=${distMonth}&distYear=${distYear}`
        ),
      ]);

      if (!schemeRes.ok) throw new Error("Failed to fetch Scheme Details");
      if (!districtRes.ok) throw new Error("Failed to fetch District Details");

      const schemeJson = await schemeRes.json();
      const districtJson = await districtRes.json();

      setSchemeData(schemeJson);
      setDistrictData(districtJson);
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [distMonth, distYear]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("EKYC Report");

    const totalColumns = 2 + schemeData.length * 9;

    const titleRow = worksheet.addRow([
      `EKYC Card Unit Status for ${distMonthName}'${distYear} for Date: ${
        districtData[0]?.data_date
          ? new Date(districtData[0].data_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : ""
      } `,
    ]);

    worksheet.mergeCells(1, 1, 1, totalColumns);

    titleRow.eachCell((cell) => {
      cell.font = { bold: true, size: 14 };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    // Define vertical headers
    const subHeaders = [
      "Total\nCards",
      "Total\nUnits",
      "Fully\ne-KYC\nDone\nRC",
      "Fully\ne-KYC\nDone\nUnits",
      "Partial\ne-KYC\nDone\nRC",
      "Partial\ne-KYC\nDone\nUnits",
      "Partial\ne-KYC\nNot\nDone\nUnits",
      "e-KYC\nNot\nDone\nRC",
      "e-KYC\nNot\nDone\nUnits",
    ];

    // Header row 1
    let headerRow1 = ["S.No", "District"];
    schemeData.forEach((scheme) => {
      for (let i = 0; i < subHeaders.length; i++) {
        headerRow1.push(scheme.schemename);
      }
    });
    const row1 = worksheet.addRow(headerRow1);

    // Header row 2 (sub headers)
    let headerRow2 = ["", ""];
    schemeData.forEach(() => {
      headerRow2.push(...subHeaders);
    });
    const row2 = worksheet.addRow(headerRow2);

    worksheet.mergeCells(2, 1, 3, 1); // S.No
    worksheet.mergeCells(2, 2, 3, 2); // District

    let colStart = 3;
    schemeData.forEach(() => {
      worksheet.mergeCells(2, colStart, 2, colStart + subHeaders.length - 1);
      colStart += subHeaders.length;
    });

    [row1, row2].forEach((row) => {
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Data rows
    districtData.forEach((item, index) => {
      let rowData = [index + 1, item.dist_name_en];
      schemeData.forEach((scheme) => {
        rowData.push(
          item[`total_rc_${scheme.schemeid}`],
          item[`total_units_${scheme.schemeid}`],
          item[`fully_ekyc_completed_rc_${scheme.schemeid}`],
          item[`fully_ekyc_done_units_${scheme.schemeid}`],
          item[`partial_ekyc_done_rc_${scheme.schemeid}`],
          item[`partial_ekyc_done_units_${scheme.schemeid}`],
          item[`partial_ekyc_not_done_units_${scheme.schemeid}`],
          item[`ekyc_not_done_rc_${scheme.schemeid}`],
          item[`ekyc_not_done_units_${scheme.schemeid}`]
        );
      });
      worksheet.addRow(rowData);
    });

    // Total row
    let totalRowData = ["", "TOTAL"];
    schemeData.forEach((scheme) => {
      totalRowData.push(
        getSchemeTotals(scheme.schemeid, "total_rc"),
        getSchemeTotals(scheme.schemeid, "total_units"),
        getSchemeTotals(scheme.schemeid, "fully_ekyc_completed_rc"),
        getSchemeTotals(scheme.schemeid, "fully_ekyc_done_units"),
        getSchemeTotals(scheme.schemeid, "partial_ekyc_done_rc"),
        getSchemeTotals(scheme.schemeid, "partial_ekyc_done_units"),
        getSchemeTotals(scheme.schemeid, "partial_ekyc_not_done_units"),
        getSchemeTotals(scheme.schemeid, "ekyc_not_done_rc"),
        getSchemeTotals(scheme.schemeid, "ekyc_not_done_units")
      );
    });
    const totalRow = worksheet.addRow(totalRowData);
    totalRow.font = { bold: true };

    worksheet.columns.forEach((col) => {
      col.width = 18;
    });
    worksheet.views = [{ state: "frozen", ySplit: 3 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `EKYC_District_Report_${distMonthName}_${distYear}.xlsx`);
  };

  // HTML Table Sub Headers for vertical stacking
  const verticalSubHeaders = [
    "Total<br/>Cards",
    "Total<br/>Units",
    "Fully<br/>e-KYC<br/>Done<br/>RC",
    "Fully<br/>e-KYC<br/>Done<br/>Units",
    "Partial<br/>e-KYC<br/>Done<br/>RC",
    "Partial<br/>e-KYC<br/>Done<br/>Units",
    "Partial<br/>e-KYC<br/>Not<br/>Done<br/>Units",
    "e-KYC<br/>Not<br/>Done<br/>RC",
    "e-KYC<br/>Not<br/>Done<br/>Units",
  ];

  return (
    <>
      {schemeData && schemeData.length > 0 ? (
        <div style={{ justifyContent: "center", marginBottom: "70px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              margin: "0 auto 20px auto",
            }}
          >
            <div style={{ width: "20px" }}></div>

            <div
              style={{
                overflowY: "auto",
                overflowX: "auto",
                width: "100%",
                margin: "0 auto",
                border: "1px solid #ccc",
              }}
            >
              <table
                width="90%"
                border="1"
                cellPadding="5"
                cellSpacing="0"
                style={{ minWidth: "100%", width: "max-content" }}
              >
                <thead style={{ position: "sticky", top: 0 }}>
                  <tr className="tableheader">
                    <th colSpan={2 + schemeData?.length * 9}>
                      EKYC Card Unit Status for {distMonthName}'{distYear} for
                      Date:{" "}
                      {districtData[0]?.data_date
                        ? new Date(
                            districtData[0].data_date
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : ""}{" "}
                    </th>
                  </tr>
                  <tr className="tableheader">
                    <th rowSpan={2}>S.No</th>
                    <th rowSpan={2}>District</th>

                    {/* Scheme headers */}
                    {schemeData.map((scheme, index) => (
                      <th key={index} colSpan={9} style={{ textAlign: "center" }}>
                        {scheme.schemename}
                      </th>
                    ))}
                  </tr>
                  <tr className="tableheader">
                    {schemeData.map((scheme) =>
                      verticalSubHeaders.map((header, idx) => (
                        <th
                          key={`${scheme.schemeid}-${idx}`}
                          style={{ textAlign: "center", whiteSpace: "pre-line" }}
                          dangerouslySetInnerHTML={{ __html: header }}
                        ></th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody>
                  {districtData.map((item, index) => (
                    <tr key={index}>
                      <td style={{ textAlign: "center" }}>{index + 1}</td>
                      <td>
                        <a
                          style={{ cursor: "pointer", color: "blue" }}
                          onClick={() =>
                            handleDistClick(item.dist_code, item.dist_name_en)
                          }
                        >
                          {item.dist_name_en}
                        </a>
                      </td>
                      {schemeData.map((scheme) => (
                        <React.Fragment key={scheme.schemeid}>
                          <td style={{ textAlign: "right" }}>
                            {item[`total_rc_${scheme.schemeid}`]}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {item[`total_units_${scheme.schemeid}`]}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {item[`fully_ekyc_completed_rc_${scheme.schemeid}`]}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {item[`fully_ekyc_done_units_${scheme.schemeid}`]}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {item[`partial_ekyc_done_rc_${scheme.schemeid}`]}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {item[`partial_ekyc_done_units_${scheme.schemeid}`]}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {item[`partial_ekyc_not_done_units_${scheme.schemeid}`]}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {item[`ekyc_not_done_rc_${scheme.schemeid}`]}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {item[`ekyc_not_done_units_${scheme.schemeid}`]}
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
                    <td colSpan={2} style={{ textAlign: "center" }}>
                      TOTAL
                    </td>
                    {schemeData.map((scheme) => (
                      <React.Fragment key={scheme.schemeid}>
                        <td style={{ textAlign: "right" }}>
                          {getSchemeTotals(scheme.schemeid, "total_rc")}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {getSchemeTotals(scheme.schemeid, "total_units")}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {getSchemeTotals(scheme.schemeid, "fully_ekyc_completed_rc")}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {getSchemeTotals(scheme.schemeid, "fully_ekyc_done_units")}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {getSchemeTotals(scheme.schemeid, "partial_ekyc_done_rc")}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {getSchemeTotals(scheme.schemeid, "partial_ekyc_done_units")}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {getSchemeTotals(scheme.schemeid, "partial_ekyc_not_done_units")}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {getSchemeTotals(scheme.schemeid, "ekyc_not_done_rc")}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {getSchemeTotals(scheme.schemeid, "ekyc_not_done_units")}
                        </td>
                      </React.Fragment>
                    ))}
                  </tr>
                </tfoot>
              </table>

              <div style={{ marginTop: "20px", textAlign: "center" }}>
                <button onClick={() => handleDownloadExcel()} className="button button2">
                  Download Excel
                </button>
              </div>
            </div>
          </div>

          <br />

          {selectedDistrict && (
            <EKYCCardsUnitsMandal
              key={selectedDistrict.distCode}
              distMonth={distMonth}
              distYear={distYear}
              distMonthName={distMonthName}
              distCode={selectedDistrict.distCode}
              distName={selectedDistrict.distName}
            />
          )}
        </div>
      ) : (
        <div
          className="mainhead"
          align="center"
          style={{ textDecoration: "none" }}
        >
          No data found
        </div>
      )}
    </>
  );
};

export default EKYCCardsUnitsDistricts;
